import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  ImagePlus, Trash2, X, CheckCircle2, AlertCircle,
  ToggleLeft, ToggleRight, ImageOff,
} from 'lucide-react';
import { bannersAPI } from '../../api/banners';
import PageHeader from '../../components/ui/PageHeader';
import Button     from '../../components/ui/Button';
import Input      from '../../components/ui/Input';

// ── Helpers ──────────────────────────────────────────────────
const formatDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

// Storage::url() in Laravel returns a relative path like /storage/banners/file.jpg.
// Prepend the backend origin so the browser loads from the correct server.
const BACKEND_ORIGIN = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api')
  .replace(/\/api$/, '');

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_ORIGIN}${url}`;
};

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES  = ['image/jpeg', 'image/jpg', 'image/png'];

// ── Skeleton ─────────────────────────────────────────────────
function BannerSkeleton() {
  return (
    <div className="card flex flex-col gap-3">
      <div className="skeleton h-36 w-full rounded-xl" />
      <div className="flex items-center justify-between">
        <div className="skeleton h-3.5 w-32 rounded" />
        <div className="skeleton h-6 w-16 rounded-full" />
      </div>
    </div>
  );
}

// ── Delete confirm sheet ──────────────────────────────────────
function DeleteSheet({ banner, onClose, onConfirm, deleting }) {
  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={!deleting ? onClose : undefined}
    >
      <div
        className="w-full max-w-app animate-fade-up rounded-t-3xl p-6 pb-8"
        style={{
          background: 'linear-gradient(180deg,#0e2060 0%,#060f38 100%)',
          border: '1px solid rgba(212,160,23,0.2)',
          borderBottom: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl flex-shrink-0"
                 style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}>
              <Trash2 size={16} style={{ color: '#fca5a5' }} />
            </div>
            <div>
              <h3 className="font-serif text-base font-semibold text-white">Delete Banner</h3>
              <p className="text-xs mt-0.5 truncate max-w-[200px]"
                 style={{ color: 'rgba(255,255,255,0.4)' }}>
                {banner.title}
              </p>
            </div>
          </div>
          {!deleting && (
            <button onClick={onClose}
                    className="flex h-7 w-7 items-center justify-center rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.08)' }}>
              <X size={14} className="text-white" />
            </button>
          )}
        </div>

        <p className="mb-5 text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
          This will permanently delete the banner image from storage. This action cannot be undone.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={deleting}
            className="flex-1 rounded-2xl py-3.5 text-sm font-semibold transition-all active:scale-95 disabled:opacity-50"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl py-3.5
                       text-sm font-semibold transition-all active:scale-95
                       disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg,#dc2626 0%,#b91c1c 100%)', color: '#fff' }}
          >
            {deleting
              ? <span className="inline-block h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              : <><Trash2 size={14} /> Delete</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Banner card ───────────────────────────────────────────────
function BannerCard({ banner, onDelete }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div className="card flex flex-col gap-3">
      {/* Image preview */}
      <div className="relative w-full overflow-hidden rounded-xl"
           style={{ height: '144px', background: 'rgba(255,255,255,0.04)' }}>
        {!imgError ? (
          <img
            src={resolveImageUrl(banner.image_url)}
            alt={banner.title}
            className="h-full w-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center flex-col gap-2">
            <ImageOff size={24} style={{ color: 'rgba(255,255,255,0.2)' }} />
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Image unavailable</p>
          </div>
        )}

        {/* Active badge overlay */}
        <div className="absolute top-2 left-2">
          <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold"
                style={{
                  background: banner.is_active ? 'rgba(5,150,105,0.85)' : 'rgba(100,100,100,0.8)',
                  color: banner.is_active ? '#d1fae5' : 'rgba(255,255,255,0.6)',
                  backdropFilter: 'blur(4px)',
                }}>
            {banner.is_active
              ? <ToggleRight size={11} />
              : <ToggleLeft  size={11} />}
            {banner.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Info row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">{banner.title}</p>
          <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Added {formatDate(banner.created_at)}
          </p>
        </div>
        <button
          onClick={() => onDelete(banner)}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl
                     transition-all active:scale-90"
          style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}
          aria-label="Delete banner"
        >
          <Trash2 size={14} style={{ color: '#fca5a5' }} />
        </button>
      </div>
    </div>
  );
}

// ── Add Banner Form ───────────────────────────────────────────
function AddBannerForm({ onAdded }) {
  const fileInputRef = useRef(null);

  const [form,       setForm]       = useState({ title: '', is_active: true });
  const [imageFile,  setImageFile]  = useState(null);
  const [preview,    setPreview]    = useState(null);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError,   setApiError]   = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setErrors((p) => ({ ...p, image: 'Only JPG and PNG files are allowed' }));
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setErrors((p) => ({ ...p, image: 'File size must not exceed 5 MB' }));
      return;
    }

    setImageFile(file);
    setPreview(URL.createObjectURL(file));
    setErrors((p) => ({ ...p, image: '' }));
    setApiError('');
  };

  const clearImage = () => {
    setImageFile(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validate = () => {
    const errs = {};
    if (!form.title.trim())  errs.title = 'Title is required';
    if (!imageFile)          errs.image = 'Please select a banner image';
    return errs;
  };

  // POST /api/admin/banners  (multipart/form-data)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const fd = new FormData();
    fd.append('title',     form.title.trim());
    fd.append('image',     imageFile);
    fd.append('is_active', form.is_active ? '1' : '0');

    setSubmitting(true);
    setApiError('');
    try {
      const { data } = await bannersAPI.add(fd);
      if (data.status) {
        onAdded(data.data);
        setForm({ title: '', is_active: true });
        clearImage();
      } else {
        setApiError(data.message || 'Failed to add banner.');
      }
    } catch (err) {
      setApiError(
        err?.response?.data?.message ||
        err?.response?.data?.errors?.title?.[0] ||
        err?.response?.data?.errors?.image?.[0] ||
        'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg flex-shrink-0"
             style={{ background: 'rgba(212,160,23,0.15)' }}>
          <ImagePlus size={14} style={{ color: '#d4a017' }} />
        </div>
        <h3 className="font-serif text-sm font-semibold text-white">Add New Banner</h3>
      </div>

      {apiError && (
        <div className="flex items-start gap-2 rounded-xl px-3 py-2.5 text-xs"
             style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
          <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
          <span>{apiError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">

        {/* Title */}
        <Input
          label="Banner Title"
          type="text"
          name="title"
          placeholder="e.g. Summer Investment Offer"
          value={form.title}
          onChange={(e) => {
            setForm((p) => ({ ...p, title: e.target.value }));
            if (errors.title) setErrors((p) => ({ ...p, title: '' }));
            if (apiError) setApiError('');
          }}
          error={errors.title}
          maxLength={255}
        />

        {/* Image upload */}
        <div className="flex flex-col gap-1.5">
          <label className="input-label">Banner Image (JPG / PNG, max 5 MB)</label>

          {!preview ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-2 rounded-xl border-2
                         border-dashed py-8 transition-all"
              style={{
                borderColor: errors.image ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.15)',
                background: 'rgba(255,255,255,0.03)',
              }}
            >
              <ImagePlus size={24} style={{ color: 'rgba(255,255,255,0.3)' }} />
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
                Tap to select image
              </span>
            </button>
          ) : (
            <div className="relative rounded-xl overflow-hidden" style={{ height: '140px' }}>
              <img src={preview} alt="Preview"
                   className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={clearImage}
                className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center
                           rounded-full transition-opacity hover:opacity-80"
                style={{ background: 'rgba(0,0,0,0.6)' }}
              >
                <X size={13} className="text-white" />
              </button>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={handleFileChange}
            className="hidden"
          />
          {errors.image && (
            <p className="text-xs" style={{ color: '#fca5a5' }}>{errors.image}</p>
          )}
        </div>

        {/* is_active toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-white">Set as Active</p>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Active banners are shown to users
            </p>
          </div>
          <button
            type="button"
            onClick={() => setForm((p) => ({ ...p, is_active: !p.is_active }))}
            className="transition-all active:scale-95"
          >
            {form.is_active
              ? <ToggleRight size={32} style={{ color: '#d4a017' }} />
              : <ToggleLeft  size={32} style={{ color: 'rgba(255,255,255,0.3)' }} />
            }
          </button>
        </div>

        <Button type="submit" variant="gold" fullWidth loading={submitting}>
          Upload Banner
        </Button>

      </form>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function BannersPage() {
  const [banners,   setBanners]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [toDelete,  setToDelete]  = useState(null);
  const [deleting,  setDeleting]  = useState(false);
  const [toast,     setToast]     = useState(null); // { type, message }

  // GET /api/admin/banners
  const fetchBanners = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await bannersAPI.getList();
      if (data.status) setBanners(data.data.banners);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load banners.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBanners(); }, [fetchBanners]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // After add — prepend to list
  const handleAdded = (newBanner) => {
    setBanners((prev) => [newBanner, ...prev]);
    setToast({ type: 'success', message: 'Banner added successfully' });
  };

  // DELETE /api/admin/banners/{id}
  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      const { data } = await bannersAPI.remove(toDelete.id);
      if (data.status) {
        setBanners((prev) => prev.filter((b) => b.id !== toDelete.id));
        setToast({ type: 'success', message: 'Banner deleted successfully' });
        setToDelete(null);
      }
    } catch (err) {
      setToast({
        type: 'error',
        message: err?.response?.data?.message || 'Failed to delete banner.',
      });
      setToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-0">
      <PageHeader title="Banners" />

      <div className="flex flex-col gap-4 p-4">

        {/* ── Toast ── */}
        {toast && (
          <div
            className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm animate-fade-in"
            style={{
              background: toast.type === 'success' ? 'rgba(5,150,105,0.15)' : 'rgba(239,68,68,0.12)',
              border: `1px solid ${toast.type === 'success' ? 'rgba(5,150,105,0.35)' : 'rgba(239,68,68,0.3)'}`,
              color: toast.type === 'success' ? '#6ee7b7' : '#fca5a5',
            }}
          >
            {toast.type === 'success'
              ? <CheckCircle2 size={15} className="flex-shrink-0" />
              : <AlertCircle  size={15} className="flex-shrink-0" />}
            <span>{toast.message}</span>
          </div>
        )}

        {/* ── Add banner form ── */}
        <AddBannerForm onAdded={handleAdded} />

        {/* ── Divider ── */}
        <div className="divider">Current Banners</div>

        {/* ── Error ── */}
        {error && (
          <div className="card py-6 text-sm text-center" style={{ color: '#fca5a5' }}>
            {error}
          </div>
        )}

        {/* ── Loading ── */}
        {loading && !error && (
          <div className="flex flex-col gap-4">
            {[1, 2, 3].map((i) => <BannerSkeleton key={i} />)}
          </div>
        )}

        {/* ── Banner list ── */}
        {!loading && !error && banners.length > 0 && (
          <div className="flex flex-col gap-4">
            {banners.map((banner) => (
              <BannerCard
                key={banner.id}
                banner={banner}
                onDelete={setToDelete}
              />
            ))}
          </div>
        )}

        {/* ── Empty state ── */}
        {!loading && !error && banners.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full"
                 style={{ background: 'rgba(255,255,255,0.06)' }}>
              <ImagePlus size={24} style={{ color: 'rgba(255,255,255,0.3)' }} />
            </div>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
              No banners added yet
            </p>
          </div>
        )}

      </div>

      {/* ── Delete confirmation sheet ── */}
      {toDelete && (
        <DeleteSheet
          banner={toDelete}
          onClose={() => !deleting && setToDelete(null)}
          onConfirm={handleDeleteConfirm}
          deleting={deleting}
        />
      )}
    </div>
  );
}
